import { api, apiErrorMessage, businessDayLabel } from '@bemor/web-kit';
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    CardTitle,
    Field,
    TimezoneSelect,
} from '@bemor/web-kit/ui';
import { useState } from 'react';

import type { Tenant } from '@/api/types';
import { useWrite } from '@/lib/queries';

/**
 * BM-143: the tenant's business time zone, the day every stored period is cut on (daily totals, tax periods, reserve
 * release, the ledger close, statements). Changeable until the first period closes; after that Identity refuses the
 * change (`timezone_locked`) and the field is read-only with the reason.
 */
export function TimezoneSection({
    tenant,
    editable,
}: {
    tenant: Tenant;
    editable: boolean;
}) {
    const current = tenant.timezone ?? 'America/New_York';
    const locked = tenant.timezone_locked === true;
    const [zone, setZone] = useState<string>(current);
    const [error, setError] = useState<string | null>(null);
    const save = useWrite(
        async () =>
            (
                await api().patch<Tenant>(`/staff/tenants/${tenant.id}`, {
                    version: tenant.version,
                    timezone: zone,
                })
            ).data,
        ['/staff/tenants'],
    );
    const canEdit = editable && !locked;
    return (
        <Card>
            <CardHeader>
                <CardTitle className="text-sm font-medium">
                    Business time zone
                </CardTitle>
            </CardHeader>
            <CardContent>
                <form
                    method="post"
                    className="grid grid-cols-1 gap-3 md:grid-cols-3"
                    data-testid="tenant-timezone"
                    onSubmit={(e) => {
                        e.preventDefault();
                        setError(null);
                        save.mutate(undefined, {
                            onError: (err) => setError(apiErrorMessage(err)),
                        });
                    }}
                >
                    <Field
                        id="tenant-timezone"
                        label="Business time zone"
                        hint={
                            locked
                                ? 'Locked: a period has closed on these days. Changing it now is an engineering migration.'
                                : `Daily totals, tax periods and statements are cut on ${businessDayLabel(zone)}. It locks once the first period closes.`
                        }
                        className="md:col-span-2"
                    >
                        <TimezoneSelect
                            id="tenant-timezone"
                            value={zone}
                            onChange={(v) => setZone(v ?? current)}
                            label="Business time zone"
                            disabled={!canEdit}
                            testId="tenant-timezone-select"
                        />
                    </Field>
                    {canEdit ? (
                        <div className="flex items-center gap-3 md:col-span-3">
                            <Button
                                type="submit"
                                size="sm"
                                disabled={zone === current || save.isPending}
                            >
                                Save time zone
                            </Button>
                            {error ? (
                                <p
                                    role="alert"
                                    className="text-destructive text-xs"
                                >
                                    {error}
                                </p>
                            ) : null}
                        </div>
                    ) : null}
                </form>
            </CardContent>
        </Card>
    );
}
